import { useState } from 'react';
import { Box, Stack, Typography, Tab } from '@mui/material';
import { TabContext, TabPanel, TabList } from '@mui/lab';
import {
  PendingOutlined,
  TrackChangesOutlined,
  CakeOutlined,
  CardGiftcardOutlined,
  DateRangeOutlined,
  TimerOutlined,
} from '@mui/icons-material';

/**
 * NotificationPopoverList Component
 *
 * This component displays a list of notifications in a tabbed interface.
 * It categorizes notifications into different types and renders them accordingly.
 *
 * @param {Object} props - The component props
 * @param {Object} props.notificationData - The data containing notifications
 * @param {Array} props.notificationData.notifications - An array of notification objects
 * @returns {JSX.Element} The rendered NotificationPopoverList component
 */
const NotificationPopoverList = ({
  notificationData = { notifications: [] },
}) => {
  /**
   * Renders notification items based on the given type.
   * @param {Object} items - The notification data object containing an array of notifications.
   * @param {string} type - The type of notifications to render.
   * @returns {JSX.Element[] | null} An array of JSX elements representing the notification items, or null if no items match.
   */
  const renderNotificationItems = (items: any, type: any) => {
    if (!items?.notifications) return null;
    if (type === 'Pending Checkout') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <PendingOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></PendingOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  Pending Checkout for{' '}
                  <b> {item?.notification?.content?.guestName}</b> Folio no:{' '}
                  <b> {item?.notification?.content?.folioNumber}</b>
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    } else if (type === 'Traces') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <TrackChangesOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></TrackChangesOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  <b>{item?.notification?.content?.message}</b>
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  {item?.notification?.content?.trace_target}
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    } else if (type === 'Birthday') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <CakeOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></CakeOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  <b>{item}</b>
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    } else if (type === 'Wedding Anniversary') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <CardGiftcardOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></CardGiftcardOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  <b>{item}</b>
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    } else if (type === 'Option Date') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <DateRangeOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></DateRangeOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  <b>{item}</b>
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    } else if (type === 'Waitlist') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <TimerOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></TimerOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  Waitlist for <b>{item?.notification?.content?.guestName}</b>{' '}
                  Booking ID: <b>{item?.notification?.content?.bookingID}</b>
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    } else if (type === 'For Cancellation') {
      return items?.notifications
        .filter((item: any) => item?.notification?.notificationType === type)
        .map((item: any) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e0e0e0',
                maxWidth: '470px',
              }}
            >
              <PendingOutlined
                sx={{ marginRight: '8px', color: '#2B3493' }}
              ></PendingOutlined>
              <Stack sx={{ flexGrow: 1 }}>
                <Typography>
                  Cancellation for{' '}
                  <b>{item?.notification?.content?.guestName}</b> Booking ID:{' '}
                  <b>{item?.notification?.content?.bookingID}</b>
                </Typography>
              </Stack>
            </Box>
          </>
        ));
    }
  };

  /**
   * Array of tab data objects containing label, value, and type for each notification category
   * @type {Array<{label: string, value: string, type: string}>}
   */
  const tabData = [
    { label: 'Pending Checkout', value: '1', type: 'Pending Checkout' },
    { label: 'Traces', value: '2', type: 'Traces' },
    { label: 'Birthday', value: '3', type: 'Birthday' },
    { label: 'Wedding Anniversary', value: '4', type: 'Wedding Anniversary' },
    { label: 'Option Date', value: '5', type: 'Option Date' },
    { label: 'Waitlist', value: '6', type: 'Waitlist' },
    { label: 'For Cancellation', value: '7', type: 'For Cancellation' },
  ];

  /**
   * Filtered tab data based on the presence of notifications for each type
   * @type {Array<{label: string, value: string, type: string}>}
   */
  const filteredTabData = tabData.filter((tab) =>
    notificationData?.notifications?.some(
      (item: any) => item?.notification?.notificationType === tab.type,
    ),
  );

  /**
   * State to keep track of the currently selected tab value
   * @type {[string, function]} listValue - The current tab value
   * @type {function} setListValue - Function to update the tab value
   */
  const [listValue, setListValue] = useState(
    filteredTabData.length > 0 ? filteredTabData[0].value : '',
  );

  // useEffect(() => {
  //   if (filteredTabData.length > 0) {
  //     setListValue(filteredTabData[0].value);
  //   }
  // }, [filteredTabData]);

  /**
   * Handles the change of tabs
   * @param {Event} event - The event object
   * @param {string} newValue - The new tab value
   */
  const handleChange = (event: any, newValue: any) => {
    console.log(event);
    setListValue(newValue);
  };

  return (
    <Stack sx={{ border: 0, maxHeight: '430px' }}>
      <TabContext value={listValue}>
        <Box sx={{ p: '0px 16px 0px 16px', maxWidth: '470px', width: '470px' }}>
          <Stack direction='row' justifyContent='space-between' p='8px 0'>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Notifications
            </Typography>
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            {filteredTabData.length === 0 ? (
              <Box
                sx={{
                  color: 'grey',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                No Notifications
              </Box>
            ) : (
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  overflow: 'hidden',
                }}
              >
                <TabList
                  onChange={handleChange}
                  aria-label='scrollable auto tabs example'
                  variant='scrollable'
                  scrollButtons='auto'
                  allowScrollButtonsMobile
                >
                  {filteredTabData.map((tab, index) => (
                    <Tab key={index} label={tab.label} value={tab.value} />
                  ))}
                  {/* Add more tabs as needed */}
                </TabList>
              </Box>
            )}
          </Stack>
        </Box>
        {filteredTabData.length > 0 ? (
          <Box sx={{ overflowY: 'auto' }}>
            {filteredTabData.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant='gray'>No Notifications</Typography>
              </Box>
            ) : (
              filteredTabData.map((tab) => (
                <TabPanel key={tab.value} value={tab.value}>
                  {renderNotificationItems(notificationData, tab.type)}
                </TabPanel>
              ))
            )}
          </Box>
        ) : (
          []
        )}
      </TabContext>
    </Stack>
  );
};

export default NotificationPopoverList;
